export type SessionProgress = 'pending' | 'finished' | 'cancelled';
export type SessionPaymentStatus = 'pending' | 'paid';

export const SESSION_PROGRESS: SessionProgress[] = ['pending', 'finished', 'cancelled'];

/** Who asked for a session to be called off. */
export type SessionRequester = 'patient' | 'therapist';
export const SESSION_PAYMENT_STATUSES: SessionPaymentStatus[] = ['pending', 'paid'];

export interface Session {
    id: string;
    title: string;
    date: string;
    patient_id?: string;
    couple_patient_id?: string;
    group_patient_id?: string;
    therapy_type: string;
    hour?: string;
    ubication?: string; // 'online' | 'in_person'
    type?: string; // 'individual' | 'couple' | 'group'
    duration?: number; // in minutes
    therapist_id?: string;
    template_id?: string;
    notes?: string;
    constellation_notes?: string;
    status?: 'pending' | 'processing' | 'review' | 'completed' | 'failed';
    audio_url?: string;
    analysis_skipped_reason?: string;
    transcription?: string;
    /**
     * Whether this session may be recorded and transcribed. Carried on the
     * session so the meeting room renders its switch right on load instead of
     * assuming "on" and correcting itself a moment later.
     */
    transcription_enabled?: boolean;
    /**
     * What the therapist or mentor wrote to prepare the session: the editor's
     * rich text document, as the notes. Theirs alone.
     */
    preparation?: string;
    /** For a training centre's mentoring, the course module it works on. */
    course_module_id?: string | null;
    /** In a list: that module's course and name, as they read when it was assigned. */
    course_internal_name?: string | null;
    course_module_name?: string | null;
    /** Whether the session took place — apart from `status`, the analysis's. */
    session_status?: SessionProgress;
    /** Whether it was paid for: by the payments tool, or marked by hand. */
    payment_status?: SessionPaymentStatus;
    /** For a cancelled session, who asked for it — what reports count as owed. */
    cancelled_by?: SessionRequester | null;
    analysis?: SessionAnalysis;
    triggered_alerts?: TriggeredAlert[];


    // Joined fields from API
    patient_name?: string;
    patient_last_name?: string;
    patient_1_name?: string;
    patient_1_last_name?: string;
    patient_1_id?: string;
    patient_2_name?: string;
    patient_2_last_name?: string;
    patient_2_id?: string;
    /** Everybody in a group session, for the therapist who held it. */
    group_members?: { id: string; first_name: string; last_name: string }[];
}

export interface SessionNoteHighlight {
    id: string;
    session_id: string;
    patient_id: string;
    therapist_id: string;
    text: string;
    created_at: string;
}

export interface SessionAgreement {
    id: string;
    session_id: string;
    text: string;
    order: number;
    source: 'ai' | 'manual';
    state: 'draft' | 'published';
    status: string | null;
    published_at: string | null;
    patient_comment: string | null;
    patient_comments?: string[];
}

export interface SessionAnalysis {
    main_topic?: string;
    emotional_status?: string;
    relevant_behaviors?: string;
    therapeutic_advances?: string;
    risks?: string;
    agreements?: string;
    related_sessions?: RelatedSession[];
    academic_articles?: AcademicArticle[];
    recommendations?: Recommendation[];
    triggered_alerts?: TriggeredAlert[];
    template_data?: SessionFieldData[];
}

export interface SessionFieldData {
    template_field_id: string;
    value: string;
}

export interface TriggeredAlert {
    alert_id: string;
    name: string;
    context: string;
    criticality: string;
}

export interface RelatedSession {
    session_id: string;
    title: string;
    date: string;
    similarity: number;
    therapy_type: string;
    patient_name: string;
    patient_last_name: string;
}

export interface AcademicArticle {
    id: string;
    title: string;
    author: string;
    link: string;
}

export interface Recommendation {
    id: string;
    title: string;
    description: string;
}

// --- Domain utility functions ---------------------------------
// These operate on the Session interface without mutating it.

export function isSessionCompleted(session: Session): boolean {
    return session.status === 'completed';
}

export function isSessionPast(session: Session): boolean {
    return new Date(session.date) < new Date();
}

/** Waiting for the therapist to read its notes before anything is analyzed. */
export function isSessionAwaitingReview(session: Session): boolean {
    return session.status === 'review';
}

export function canSessionBeAnalyzed(session: Session): boolean {
    // A session in review is exactly the one waiting to be analyzed: the
    // therapist reads the notes and asks for the analysis from here.
    return isSessionCompleted(session) || isSessionAwaitingReview(session);
}

/**
 * The form fills a new session's title as `YYYY-MM-DD - <patient>`. Where the
 * date and the patient are already on screen that title says nothing, so it
 * counts as no title: only one the therapist wrote is returned.
 */
const DEFAULT_TITLE = /^\d{4}-\d{2}-\d{2} - /;

export function ownSessionTitle(title: string | null | undefined): string | null {
    const trimmed = title?.trim() ?? '';
    return trimmed && !DEFAULT_TITLE.test(trimmed) ? trimmed : null;
}

export function getSessionPatientName(session: Session): string {
    if (session.type === 'group') {
        // The group's name, or its members' first names when it has none.
        return session.patient_name
            || (session.group_members ?? []).map((member) => member.first_name).join(', ');
    }
    if (session.type === 'couple') {
        const p1 = [session.patient_1_name, session.patient_1_last_name].filter(Boolean).join(' ');
        const p2 = [session.patient_2_name, session.patient_2_last_name].filter(Boolean).join(' ');
        return [p1, p2].filter(Boolean).join(' & ');
    }
    return [session.patient_name, session.patient_last_name].filter(Boolean).join(' ');
}

export function getSessionDurationInHours(session: Session): number {
    return (session.duration ?? 60) / 60;
}
